import { zxcvbn, zxcvbnOptions } from "@zxcvbn-ts/core";
import * as zxcvbnCommonPackage from "@zxcvbn-ts/language-common";
import * as zxcvbnEnPackage from "@zxcvbn-ts/language-en";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import layoutStyle from "components/layout/layout.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import style from "components/login/create-new-password/create-new-password.scss";
import CreateNewPasswordForm from "components/login/create-new-password/CreateNewPasswordForm";
import ValidatePasswordResetLink from "components/login/ValidatePasswordResetLink";
import { Logo } from "components/logo/Logo";
import modalStyle from "components/modal/modal.scss";
import { LOGIN_ROUTE } from "components/router/Routes";
import Heading from "components/typography/heading/Heading";
import { authenticationService } from "services/security/AuthenticationService";
import { getUser } from "services/user/userSessionRepository";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

zxcvbnOptions.setOptions({
    dictionary: {
        ...zxcvbnCommonPackage.dictionary,
        ...zxcvbnEnPackage.dictionary,
    },
    graphs: zxcvbnCommonPackage.adjacencyGraphs,
    translations: zxcvbnEnPackage.translations,
});

interface FulfilledPasswordProvisions {
    length: boolean;
    containsUpper: boolean;
    containsLower: boolean;
    containsDigit: boolean;
    containsSymbol: boolean;
    isStrong: boolean;
    noEmailUsername: boolean;
}

interface Props {
    changePassword?: boolean;
    setChangePasswordFormVisibility?: () => void;
    errorModalVisible?: (value: boolean) => void;
}

export interface ProvisionProperties {
    fulfilled: () => boolean;
    translationKey: string;
}

enum Provision {
    LENGTH,
    UPPER,
    LOWER,
    DIGIT,
    SYMBOL,
    STRENGTH,
    NO_EMAIL_USERNAME,
}

const NO_FULFILLED_PROVISIONS = {
    length: false,
    containsUpper: false,
    containsLower: false,
    containsDigit: false,
    containsSymbol: false,
    isStrong: false,
    noEmailUsername: false,
};

enum RequestResult {
    NOT_REQUESTED,
    SUCCESS,
    FAILURE,
}

// Extracts meaningful parts from an email address to validate against the password.
// For "firstname.lastname+4565@example.com" returns ["firstname", "lastname", "4565"].
const extractEmailParts = (email: string): string[] => {
    const username = email.split("@")[0].toLowerCase();
    const [basePart, emailTag] = username.split("+");
    return basePart
        .split(".")
        .concat(emailTag ?? [])
        .filter((part) => part.length >= 3);
};

const CreateNewPasswordView = (props?: Props | undefined): JSX.Element => {
    const { t, ready } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const history = useHistory();

    const [fulfilledPasswordProvisions, setFulfilledPasswordProvisions] =
        React.useState<FulfilledPasswordProvisions>(NO_FULFILLED_PROVISIONS);
    const [requesting, setRequesting] = React.useState<boolean>(false);
    const [validateConfirmedPassword, setvalidateConfirmedPassword] = React.useState<boolean>(false);
    const [newPassword, setNewPassword] = React.useState<string>("");
    const [confirmPassword, setConfirmPassword] = React.useState<string>("");
    const [verificationCode, setVerificationCode] = React.useState<string | null>("");
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [requestResult, setRequestResult] = React.useState<RequestResult>(RequestResult.NOT_REQUESTED);
    const [showValidationPopup, setShowValidationPopup] = React.useState<boolean>(false);
    const [validationSuccess, setValidationSuccess] = React.useState<boolean>(false);
    const [validationError, setValidationError] = React.useState<string>("");
    const [emailAddress, setEmailAddress] = React.useState<string>("");
    const [confirmationVisible, setConfirmationVisible] = React.useState<boolean>(false);
    const [isTemporaryPasswordFlow, setTemporaryPasswordFlow] = React.useState<boolean>(false);

    const validateTemporaryPassword = React.useCallback(
        (email: string, isTemporaryPassword: boolean, sentTimestamp: string) => {
            authenticationService
                .validateTemporaryPassword(email, isTemporaryPassword, sentTimestamp)
                .then(() => {
                    setValidationSuccess(true);
                    setValidationError("");
                    return true;
                })
                .catch((e) => {
                    logger.error("Failed validateTemporaryPassword exception is:", e);
                    setValidationSuccess(false);
                    setShowValidationPopup(true);
                    setValidationError(t("temporaryPasswordValidationFailureModal.title"));
                    return false;
                })
                .finally(() => {
                    setRequesting(false);
                });
        },
        [t]
    );

    React.useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const emailAddress = queryParams.get("emailAddress");
        const sentTimestamp = queryParams.get("sentTimestamp") ?? "";

        setEmailAddress(emailAddress || "");
        const isTemporaryPassword = queryParams.has("temporaryPassword");
        setTemporaryPasswordFlow(isTemporaryPassword);
        if (emailAddress) {
            validateTemporaryPassword(emailAddress, isTemporaryPassword, sentTimestamp);
        }
    }, [location.search, validateTemporaryPassword]);

    if (!ready || (requesting && !showValidationPopup)) {
        return <LoadingIndicator />;
    }

    const hideResult = () => {
        props?.setChangePasswordFormVisibility && props.setChangePasswordFormVisibility();
    };

    const showErrorModal = () => {
        props?.errorModalVisible && props.errorModalVisible(true);
    };

    let content: JSX.Element | null = null;

    const navigateToLogin = () => history.push(LOGIN_ROUTE.path);

    if (!showValidationPopup) {
        switch (requestResult) {
            case RequestResult.NOT_REQUESTED: {
                const provisionPropertiesMap: Map<Provision, ProvisionProperties> = new Map([
                    [
                        Provision.LENGTH,
                        {
                            fulfilled: () => fulfilledPasswordProvisions.length,
                            translationKey: "passwordPolicyLength",
                        },
                    ],
                    [
                        Provision.UPPER,
                        {
                            fulfilled: () => fulfilledPasswordProvisions.containsUpper,
                            translationKey: "passwordPolicyContainsUpper",
                        },
                    ],
                    [
                        Provision.LOWER,
                        {
                            fulfilled: () => fulfilledPasswordProvisions.containsLower,
                            translationKey: "passwordPolicyContainsLower",
                        },
                    ],
                    [
                        Provision.DIGIT,
                        {
                            fulfilled: () => fulfilledPasswordProvisions.containsDigit,
                            translationKey: "passwordPolicyContainsDigit",
                        },
                    ],
                    [
                        Provision.SYMBOL,
                        {
                            fulfilled: () => fulfilledPasswordProvisions.containsSymbol,
                            translationKey: "passwordPolicyContainsSymbol",
                        },
                    ],
                    [
                        Provision.STRENGTH,
                        {
                            fulfilled: () => fulfilledPasswordProvisions.isStrong,
                            translationKey: "passwordPolicyStrengthRequirements",
                        },
                    ],
                    [
                        Provision.NO_EMAIL_USERNAME,
                        {
                            fulfilled: () => fulfilledPasswordProvisions.noEmailUsername,
                            translationKey: "passwordPolicyNoPersonalInfo",
                        },
                    ],
                ]);

                const validatePassword = (value: string): void => {
                    setNewPassword(value);
                    const isStrongEnough = zxcvbn(value).score >= 3;
                    const email = props?.changePassword ? getUser()?.email : emailAddress;
                    const emailParts = extractEmailParts(email ?? "");
                    const passwordLower = value.toLowerCase();
                    setFulfilledPasswordProvisions(() => {
                        return {
                            length: value.length >= 12 && value.length <= 64,
                            containsUpper: /[A-Z]/.test(value),
                            containsLower: /[a-z]/.test(value),
                            containsDigit: /\d/.test(value),
                            containsSymbol: /(?=[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-])/.test(value),
                            isStrong: isStrongEnough,
                            noEmailUsername:
                                value.length > 0 && emailParts?.every((part) => !passwordLower.includes(part)),
                        };
                    });
                };

                const validateConfirmPassword = (value: string): void => {
                    setConfirmPassword(value);
                    setvalidateConfirmedPassword(value.length >= 10 && value.length <= 64 && newPassword === value);
                };

                const submitEnabled =
                    fulfilledPasswordProvisions.length &&
                    fulfilledPasswordProvisions.containsUpper &&
                    fulfilledPasswordProvisions.containsLower &&
                    fulfilledPasswordProvisions.containsDigit &&
                    fulfilledPasswordProvisions.containsSymbol &&
                    fulfilledPasswordProvisions.noEmailUsername &&
                    validateConfirmedPassword;

                const queryParams = new URLSearchParams(location.search);
                const emailAddress = queryParams.get("emailAddress");
                const verificationCode = queryParams.get("verificationCode");
                const temporaryPassword = queryParams.get("temporaryPassword");

                const passwordCallback = (value: string): void => {
                    if (
                        emailAddress == null ||
                        (verificationCode == null && temporaryPassword == null && temporaryPassword == "")
                    ) {
                        return;
                    }

                    setVerificationCode(verificationCode);

                    if (!temporaryPassword && !verificationCode) {
                        setRequesting(false);
                        setRequestResult(RequestResult.FAILURE);
                    } else {
                        setRequesting(true);
                        authenticationService
                            .passwordReset(emailAddress, verificationCode, temporaryPassword, value)
                            .then(() => setRequestResult(RequestResult.SUCCESS))
                            .catch((e) => {
                                logger.error("Failure when creating new password, error:", e);
                                setRequestResult(RequestResult.FAILURE);
                            })
                            .finally(() => setRequesting(false));
                    }
                };

                const updatePassword = (oldPassword: string, updatedPassword: string) => {
                    authenticationService
                        .updatePassword(oldPassword, updatedPassword)
                        .then(() => {
                            hideResult();
                            toast(
                                <SuccessIcon
                                    successClass={layoutStyle.customToastSuccessIcon}
                                    color={theme.contentBackgroundColor}
                                    text={t("Settings.updatePassword")}
                                />,
                                {
                                    closeButton: (closeToastProps) => (
                                        <CustomToastCloseButton
                                            closeToast={{ ...closeToastProps }}
                                            color={theme.iconFillColor}
                                        />
                                    ),
                                    className: layoutStyle.customToastSuccessMessage,
                                }
                            );
                        })
                        .catch((e) => {
                            let errorMessage = e;
                            let errorCode = e;
                            try {
                                const response = JSON.parse(e.message);
                                errorMessage = response.error.message;
                                errorCode = response.error.code;
                            } finally {
                                if (errorMessage === "Incorrect old password") {
                                    setErrorMessage(t("Settings.incorrectOldPassword"));
                                }
                                if (errorCode == "TOO_MANY_REQUESTS") {
                                    showErrorModal();
                                    hideResult();
                                }
                            }
                        });
                };
                content = (
                    <>
                        <div className={style.passwordPolicyText}>
                            {t("CreateNewPasswordView.passwordPolicyIntro")}
                            <ul>
                                {Array.from(provisionPropertiesMap.values()).map((provisionProperty) => {
                                    return (
                                        <li
                                            key={provisionProperty.translationKey}
                                            className={classNames({
                                                [style.applyCheckMark]: provisionProperty.fulfilled(),
                                            })}
                                        >
                                            {t(`CreateNewPasswordView.${provisionProperty.translationKey}`)}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        <CreateNewPasswordForm
                            validatePassword={validatePassword || validateConfirmPassword}
                            validateConfirmPassword={validateConfirmPassword}
                            submitDisabled={!submitEnabled || !(newPassword === confirmPassword)}
                            passwordCallback={passwordCallback}
                            provisionProperties={Array.from(provisionPropertiesMap.values())}
                            changePassword={props?.changePassword}
                            updatePasswordDetails={updatePassword}
                            errorMessage={errorMessage}
                            setChangePasswordFormVisibility={
                                showValidationPopup
                                    ? () => setShowValidationPopup(false)
                                    : props?.setChangePasswordFormVisibility
                            }
                        />
                    </>
                );
                break;
            }
            case RequestResult.SUCCESS:
            case RequestResult.FAILURE:
                content = (
                    <>
                        <div className={style.result}>
                            {requestResult == RequestResult.SUCCESS
                                ? verificationCode != null
                                    ? t("CreateNewPasswordView.requestSuccess")
                                    : t("CreateNewPasswordView.requestSuccessForNewPassword")
                                : t("CreateNewPasswordView.requestFailure")}
                        </div>
                        <div className={form.buttonContainer}>
                            <button
                                type="button"
                                className={buttons.primaryButtonWithoutIcon}
                                data-testid={testIds.common.dialog.closeButton}
                                onClick={navigateToLogin}
                            >
                                {t("CreateNewPasswordView.backToLogin")}
                            </button>
                        </div>
                    </>
                );
                break;
            default:
                throw new Error("Unsupported state");
        }
    }

    return (
        <>
            <ValidatePasswordResetLink
                isOpen={showValidationPopup}
                onClose={() => setShowValidationPopup(false)}
                isSuccess={validationSuccess}
                errorMessage={validationError}
                userEmail={emailAddress}
                onConfirmationChange={setConfirmationVisible}
                isNewUserFlow={isTemporaryPasswordFlow}
            />
            {!showValidationPopup &&
                !confirmationVisible &&
                content &&
                (props?.changePassword ? (
                    <div className={style.createNewPasswordContainer}>
                        <div className={style.passwordFormContainer}>{content}</div>
                    </div>
                ) : (
                    <div className={style.createNewPassword}>
                        <div className={style.createNewPasswordHeader}>
                            <Logo isWhiteLogo width={166} height={58} />
                        </div>
                        <div className={style.createNewPasswordContainer}>
                            <div className={style.formContainer}>
                                <Heading tag="h1" className={classNames(modalStyle.title, style.header)}>
                                    {requestResult == RequestResult.SUCCESS
                                        ? t("CreateNewPasswordView.passwordCreated")
                                        : t("CreateNewPasswordView.title")}
                                </Heading>
                                {content}
                            </div>
                        </div>
                    </div>
                ))}
        </>
    );
};

export default CreateNewPasswordView;
